/**
 * Address of the kiosk screen; it has no menu item and is entered manually
 * @type {string}
 */
export const KIOSK_PATH = '/kiosk'

/**
 * Name of the kiosk option that shows a position chart per group
 * @type {string}
 */
export const OPTION_CHARTS = 'position_charts'

/**
 * Name of the kiosk option that shows the weather: the forecast, and the weather in the charts
 * @type {string}
 */
export const OPTION_WEATHER = 'weather'

/**
 * Name of the kiosk option that fits the groups to the screen again whenever they change
 * @type {string}
 */
export const OPTION_FIT = 'fit'

/**
 * Name of the kiosk option that holds the zoom
 * @type {string}
 */
export const OPTION_ZOOM = 'zoom'

/**
 * Zoom of the kiosk screen, 1 for the normal size of the app; the slider moves a step at a time,
 * the quick zoom buttons a quick step
 * @type {{ min: number, max: number, step: number, quick: number, initial: number }}
 */
export const ZOOM = { min: 0.25, max: 1.5, step: 0.01, quick: 0.05, initial: 1 }

/**
 * Keep a zoom within its range, rounded to whole percentages
 * @param {number} zoom zoom to keep in range
 * @returns {number} zoom
 */
const toZoom = ( zoom ) => Number( Math.min( ZOOM.max, Math.max( ZOOM.min, zoom ) ).toFixed( 2 ) )

/**
 * Get whether the kiosk screen shows a position chart per group
 * @param {Object} options kiosk options
 * @returns {boolean} whether the charts are shown
 */
export const getShowCharts = ( options = {} ) => Boolean( options && options[ OPTION_CHARTS ] === true )

/**
 * Get whether the kiosk screen shows the weather; on unless turned off
 * @param {Object} options kiosk options
 * @returns {boolean} whether the weather is shown
 */
export const getShowWeather = ( options = {} ) => !options || options[ OPTION_WEATHER ] !== false

/**
 * Get whether the kiosk screen fits the groups to the screen by itself: from fitting once until the
 * zoom is chosen by hand
 * @param {Object} options kiosk options
 * @returns {boolean} whether the zoom fits by itself
 */
export const getAutoFit = ( options = {} ) => Boolean( options && options[ OPTION_FIT ] === true )

/**
 * Get the chosen zoom of the kiosk screen, the initial zoom when none is chosen
 * @param {Object} options kiosk options
 * @returns {number} zoom, 1 for the normal size
 */
export const getZoom = ( options = {} ) =>
{
  const zoom = options && options.hasOwnProperty( OPTION_ZOOM ) ? options[ OPTION_ZOOM ] : ZOOM.initial
  if ( typeof zoom !== 'number' || isNaN( zoom ) ) return ZOOM.initial
  return toZoom( zoom )
}

/**
 * Zoom in or out by a number of quick steps
 * @param {number} zoom current zoom
 * @param {number} steps quick steps, negative to zoom out
 * @returns {number} new zoom, within the range
 */
export const stepZoom = ( zoom, steps ) => toZoom( zoom + steps * ZOOM.quick )

/**
 * Show a zoom as a percentage
 * @param {number} zoom zoom, 1 for the normal size
 * @returns {string} percentage
 */
export const formatZoom = ( zoom ) => `${ Math.round( zoom * 100 ) }%`
